import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {GeocodedSuburb, Shop} from '../models/middleware';
import {queryClient} from '../queries/queryClient';
import {rewardsKeys} from '../queries/rewards/useRewardsAccount';
import {AccountStatus} from '../screens/rewards/types';
import apiService from '../transport/apiService';
import {
  RewardsGetAccountResponse,
  RewardsSignUpCompleteResult,
  RewardsSignUpOutcome,
  RewardsSignUpResponse,
  VerifyPhoneStaffResult,
} from '../transport/types';
import {
  getValueFromStorage,
  removeValueFromStorage,
  setValueToStorage,
} from '../utils/localStorage';

import {clearInflight, dumpInflight, loadInflight} from './rewardsInflightPersist';
import type {RootStore} from './rootStore';
const REWARDS_STORE_KEY = 'REWARDS_STORE';

// DIS-1321: per-call-site precedence policies for membership card fields.
// /account-details is Zen-authoritative (WIRE_WINS / WIRE_TRISTATE);
// /verification-lite and /verification-lite/complete keep WIRE_OR_LOCAL /
// OR_RATCHET until MW PR #565 lands.
export type MergePolicy = 'WIRE_WINS' | 'WIRE_OR_LOCAL';
export type StaffPolicy = 'WIRE_TRISTATE' | 'OR_RATCHET';

export function mergeStringField(
  local: string,
  wire: string | undefined,
  policy: MergePolicy,
): string {
  if (policy === 'WIRE_WINS') {
    return wire !== undefined ? wire : local;
  }
  return local || wire || '';
}

export function mergeStaffFlag(
  local: boolean,
  wire: boolean | undefined,
  policy: StaffPolicy,
): boolean {
  if (policy === 'WIRE_TRISTATE') {
    return wire !== undefined ? wire : local;
  }
  return !!wire || local;
}

// These are the properties that are needed to show
// the sign up success screen. Can modify as needs change
type RewardsStorePersist = Pick<
  RewardsStore,
  | 'hasRewards'
  | 'cardNumber'
  | 'firstName'
  | 'lastName'
  | 'shortcode'
  | 'shortcodeExpiresAt'
  | 'walletUrl'
  | 'memberSince'
  | 'staffStoreId'
  | 'isStaffSignup'
  | 'isLmgStaff'
  | 'isLiteMember'
>;

// This store is for the rewards data which ultimately lives in Zen

async function rewardsSetValueToStorage(data: RewardsStorePersist) {
  try {
    await setValueToStorage(REWARDS_STORE_KEY, JSON.stringify(data));
  } catch (_e) {
    throw new Error(`Failed setValueToStorage ${REWARDS_STORE_KEY}`);
  }
}

async function rewardsGetValueFromStorage() {
  try {
    const data = await getValueFromStorage(REWARDS_STORE_KEY);
    if (data) {
      return JSON.parse(data) as RewardsStorePersist;
    }
  } catch (_e) {
    throw new Error(`Failed getValueFromStorage ${REWARDS_STORE_KEY}`);
  }
  return null;
}

export class RewardsStore {
  rootStore: RootStore;

  hasRewards = false;
  // DIS-1633: this device completed a lite sign-up, which sets hasRewards but
  // never creates an MFL session, so userStore.isLoggedIn is false for this
  // member by design. Covers store-staff, LMG 'applied' and LMG 'skipped' alike.
  isLiteMember: boolean = false;
  isHydrated = false;

  firstName: string = '';
  lastName: string = '';

  /**
   * Starts with +61
   */
  phoneNumber: string = '';
  emailAddress: string = '';
  dateOfBirth: string = ''; // YYYY-MM-DD format
  suburb: Pick<GeocodedSuburb, 'locality' | 'postal_code' | 'administrative_area'> | null = null;
  signUpCode: string = '';
  isStaffSignup: boolean = false;
  staffStoreId: string | null = null;
  staffStore: Shop | null = null;
  // DIS-1190: phase 1 issues a sessionToken that phase 3 must echo. In-memory
  // only — workflow TTL is 5min and a hard reload restarts the verify flow.
  staffSessionToken: string = '';

  // DIS-1384: LMG-staff lite sign-up. Parallel to the store-staff flow (not a
  // sub-mode). isLmgStaffFlow = mode (landing-set from the invite lmgCode; drives
  // gating/persistence/CTA). isLmgStaff = badge, derived DIRECTLY from
  // lmgStaffActivation.status==='applied' (NOT a ratchet — a 'skipped' result
  // withholds the badge while the mode flag stays true). dept/state are sent
  // verbatim; lmgStaffCode is the opaque invite secret. Option lists are a
  // per-session cache (fetched from GET /rewards/lmg-staff) — never persisted.
  isLmgStaffFlow: boolean = false;
  isLmgStaff: boolean = false;
  lmgDepartment: string = '';
  lmgState: string = '';
  lmgStaffCode: string = '';
  lmgDepartments: string[] = [];
  lmgStates: string[] = [];
  lmgOptionsLoading: boolean = false;
  lmgOptionsError: boolean = false;

  // DIS-1384: both the store-staff (DIS-1190) and LMG-staff flows run the staged
  // two-call verify (verifyStaffPhone -> signUpStaff). Screens gate the staged
  // path on this rather than isStaffSignup alone.
  get usesStagedVerification(): boolean {
    return this.isStaffSignup || this.isLmgStaffFlow;
  }

  cardNumber: string = '';
  shortcode: string = '';
  shortcodeExpiresAt: string = '';
  walletUrl: string = '';
  memberSince: string = '';

  // DIS-1318: in-screen Back buttons that route to REWARDS_SIGN_IN must echo
  // the active staff/signUpCode flags so the URL query string survives and
  // RewardsSignInScreen re-renders in the correct mode.
  get signInRouteParams(): {staff?: string; signUpCode?: string; lmgCode?: string} {
    const params: {staff?: string; signUpCode?: string; lmgCode?: string} = {};
    if (this.isStaffSignup) params.staff = 'true';
    if (this.signUpCode) params.signUpCode = this.signUpCode;
    // DIS-1384: echo the invite secret so a Back-to-sign-in re-enters LMG mode.
    if (this.lmgStaffCode) params.lmgCode = this.lmgStaffCode;
    return params;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);
    runInAction(() => {
      this.load();
    });
  }

  dump = async () => {
    await rewardsSetValueToStorage({
      hasRewards: this.hasRewards,
      firstName: this.firstName,
      lastName: this.lastName,
      cardNumber: this.cardNumber,
      shortcode: this.shortcode,
      shortcodeExpiresAt: this.shortcodeExpiresAt,
      walletUrl: this.walletUrl,
      memberSince: this.memberSince,
      staffStoreId: this.staffStoreId,
      // DIS-1189: persist staff flag so MemberCard renders STAFF badge across app restarts
      isStaffSignup: this.isStaffSignup,
      // DIS-1384 (REQ-1): persist LMG badge so it survives app restart for lite users
      // (no MFL session to re-fetch /account-details). Parity with isStaffSignup.
      isLmgStaff: this.isLmgStaff,
      // DIS-1633: persist the lite-member marker so the Rewards member view
      // survives a restart for a member who has no MFL session.
      isLiteMember: this.isLiteMember,
    });
  };

  load = async () => {
    const data = await rewardsGetValueFromStorage();
    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this[key as keyof RewardsStorePersist] as any) = value;
      });
    }
    // DIS-1228: rehydrate in-flight signup fields (signUpCode, phoneNumber,
    // staffSessionToken, etc.) that are excluded from RewardsStorePersist
    // but must survive reload within a 5-minute backend workflow TTL.
    const inflight = await loadInflight();
    if (inflight) {
      runInAction(() => {
        if (!this.signUpCode) this.signUpCode = inflight.signUpCode;
        if (!this.phoneNumber) this.phoneNumber = inflight.phoneNumber;
        if (!this.isStaffSignup) this.isStaffSignup = inflight.isStaffSignup;
        if (!this.staffStoreId) this.staffStoreId = inflight.staffStoreId;
        if (!this.staffSessionToken) this.staffSessionToken = inflight.staffSessionToken;
        // DIS-1384: rehydrate LMG flow fields (option lists are re-fetched, R22b).
        if (!this.isLmgStaffFlow) this.isLmgStaffFlow = inflight.isLmgStaffFlow;
        if (!this.lmgDepartment) this.lmgDepartment = inflight.lmgDepartment;
        if (!this.lmgState) this.lmgState = inflight.lmgState;
        if (!this.lmgStaffCode) this.lmgStaffCode = inflight.lmgStaffCode;
      });
    }
    runInAction(() => {
      this.isHydrated = true;
    });
  };

  // DIS-1228: persist the in-flight slice on every setter that touches a
  // field we need to survive reload. Fire-and-forget — same posture as dump().
  private dumpInflightSnapshot = () => {
    dumpInflight({
      signUpCode: this.signUpCode,
      phoneNumber: this.phoneNumber,
      isStaffSignup: this.isStaffSignup,
      staffStoreId: this.staffStoreId,
      staffSessionToken: this.staffSessionToken,
      // DIS-1384: LMG flow fields survive reload within the 5min workflow TTL.
      // Option lists (lmgDepartments/lmgStates) are a per-session cache and are
      // deliberately excluded (R22a/R22b) — they are re-fetched on the picker.
      isLmgStaffFlow: this.isLmgStaffFlow,
      lmgDepartment: this.lmgDepartment,
      lmgState: this.lmgState,
      lmgStaffCode: this.lmgStaffCode,
    });
  };

  /**
   * Starts with +61
   */
  setPhoneNumber = (value: string) => {
    if (value?.indexOf('0') === 0) {
      value = '+61' + value.substring(1);
    }

    this.phoneNumber = value;
    this.dumpInflightSnapshot();
  };

  setFirstName = (value: string) => {
    this.firstName = value;
  };

  setLastName = (value: string) => {
    this.lastName = value;
  };

  setEmailAddress = (value: string) => {
    this.emailAddress = value;
  };

  setDateOfBirth = (value: string) => {
    this.dateOfBirth = value;
  };

  setSuburb = (value: Pick<GeocodedSuburb, 'locality' | 'postal_code' | 'administrative_area'>) => {
    this.suburb = value;
  };

  setSignUpCode = (value: string) => {
    this.signUpCode = value;
    this.dumpInflightSnapshot();
  };

  setIsStaffSignup = (value: boolean) => {
    this.isStaffSignup = value;
    this.dumpInflightSnapshot();
  };

  setStaffStoreId = (value: string | null) => {
    this.staffStoreId = value;
    this.dumpInflightSnapshot();
  };

  setStaffStore = (value: Shop | null) => {
    this.staffStore = value;
  };

  // DIS-1384: LMG mode/selection setters mirror setStaffStoreId — each persists
  // the in-flight slice so the LMG flow survives a reload. setIsLmgStaff is the
  // server-confirmed badge (not part of the in-flight slice) so it does NOT dump.
  setIsLmgStaffFlow = (value: boolean) => {
    this.isLmgStaffFlow = value;
    this.dumpInflightSnapshot();
  };

  setIsLmgStaff = (value: boolean) => {
    this.isLmgStaff = value;
  };

  setLmgDepartment = (value: string) => {
    this.lmgDepartment = value;
    this.dumpInflightSnapshot();
  };

  setLmgState = (value: string) => {
    this.lmgState = value;
    this.dumpInflightSnapshot();
  };

  setLmgStaffCode = (value: string) => {
    this.lmgStaffCode = value;
    this.dumpInflightSnapshot();
  };

  // DIS-1384: fetch the department/state option lists (GET /rewards/lmg-staff).
  // Per-session cache — never persisted (re-fetched on the picker). Distinct
  // loading/error flags so the screen can show a retry-capable error (R25).
  loadLmgStaffOptions = async () => {
    runInAction(() => {
      this.lmgOptionsLoading = true;
      this.lmgOptionsError = false;
    });
    try {
      const {data} = await apiService.rewardsGetLmgStaffOptions();
      runInAction(() => {
        this.lmgDepartments = data.departments;
        this.lmgStates = data.states;
        this.lmgOptionsLoading = false;
      });
    } catch {
      runInAction(() => {
        this.lmgOptionsError = true;
        this.lmgOptionsLoading = false;
      });
    }
  };

  // DIS-1646: clears FLOW state only. The badge flags (isStaffSignup,
  // isLmgStaff) are deliberately left alone here. This runs on every unmount of
  // RootRewardsFlowStackNavigator, including straight after a successful signup,
  // and RewardsListScreen / AccountRewardsScreen read those flags from memory.
  // Clearing them here blanks an earned badge on flow exit, and persisting that
  // clear would blank it permanently for lite staff, who have no MFL session and
  // so never re-fetch isStaff from /rewards/account-details (DIS-1189/1239/1384).
  // Staleness is prevented on flow ENTRY instead — see RewardsSignInScreen.
  resetStaffFlow = () => {
    this.staffStoreId = null;
    this.staffStore = null;
    this.staffSessionToken = '';
    // DIS-1384: clear all LMG mode/selection state on flow exit (R22c).
    this.isLmgStaffFlow = false;
    this.lmgDepartment = '';
    this.lmgState = '';
    this.lmgStaffCode = '';
    this.lmgDepartments = [];
    this.lmgStates = [];
    this.lmgOptionsLoading = false;
    this.lmgOptionsError = false;
    clearInflight();
    // DIS-1646: staffStoreId is persisted, so the clear needs writing back.
    this.dump();
  };

  setCardNumber = (value: string) => {
    this.cardNumber = value;
  };

  setShortcode = (value: string) => {
    this.shortcode = value;
  };

  setShortcodeExpiresAt = (value: string) => {
    this.shortcodeExpiresAt = value;
  };

  setWalletUrl = (value: string) => {
    this.walletUrl = value;
  };

  loadRewards = (data: RewardsSignUpResponse) => {
    runInAction(() => {
      this.hasRewards = true;
      this.firstName = data.firstName;
      this.lastName = data.lastName;
      this.cardNumber = data.cardNumber;
      this.shortcode = data.shortcode;
      this.shortcodeExpiresAt = data.shortcodeExpiresAt;
      this.walletUrl = data.walletUrl;
      this.memberSince = data.memberSince || '';
      this.dump();
    });
  };

  // DIS-1321: canonical merge point for /rewards/account-details responses.
  // /account-details is Zen-authoritative: wire wins for firstName, lastName,
  // memberSince; isStaff is tri-state (undefined keeps local, true sets,
  // false clears). Does not touch signup-only fields.
  applyAccountDetails = (data: RewardsGetAccountResponse) => {
    runInAction(() => {
      this.hasRewards = !!data.exists;
      this.cardNumber = data.cardNumber;
      this.shortcode = data.shortcode;
      this.shortcodeExpiresAt = data.shortcodeExpiresAt;
      this.walletUrl = data.walletUrl;
      this.firstName = mergeStringField(this.firstName, data.firstName, 'WIRE_WINS');
      this.lastName = mergeStringField(this.lastName, data.lastName, 'WIRE_WINS');
      this.memberSince = mergeStringField(this.memberSince, data.memberSince, 'WIRE_WINS');
      this.isStaffSignup = mergeStaffFlag(this.isStaffSignup, data.isStaff, 'WIRE_TRISTATE');
      // DIS-1384: isLmgStaff badge is Zen-authoritative on /account-details —
      // WIRE_TRISTATE (present overrides true/false, absent preserves local, R17).
      if (data.isLmgStaff !== undefined) this.isLmgStaff = data.isLmgStaff;
      this.dump();
    });
  };

  loadRewardsForUser = async (phoneNumber: string) => {
    // DIS-1187: this endpoint 401s for lite users (no MFL session) —
    // caller gates on userStore.isLoggedIn. DIS-1321: merge logic lives in
    // applyAccountDetails (Zen-authoritative WIRE_WINS / WIRE_TRISTATE).
    const result = await apiService.rewardsGetAccount({phoneNumber});
    if (result?.status === 200 && result.data) {
      this.applyAccountDetails(result.data);
    }
  };

  optOut = async (phoneNumber: string, lastName: string) => {
    const result = await apiService.rewardsOptOut({
      phoneNumber,
      lastName,
    });

    if (result) {
      runInAction(() => {
        this.reset();
      });
      // DIS-1175: reset() only clears in-memory state. Re-enabling the
      // /rewards/account query the moment hasRewards flips false must NOT
      // resurrect membership. removeQueries() forced a network refetch that,
      // under read-after-write lag (beta/prod), returned a stale exists:true —
      // applyAccountDetails then flipped hasRewards back on, so opt-out took two
      // taps. Opt-out already succeeded here, so authoritatively seed the cache
      // exists:false: the re-enabled query serves it fresh (staleTime 2min) with
      // no round-trip, and the opted-out view renders without a manual reload.
      queryClient.setQueryData<RewardsGetAccountResponse>(
        rewardsKeys.account(phoneNumber),
        prev => ({...(prev ?? ({} as RewardsGetAccountResponse)), exists: false}),
      );
      removeValueFromStorage(REWARDS_STORE_KEY);

      // reload the cart to remove rewards pricing to existing cart items
      this.rootStore.cartStore.fullyReloadOrderFromMFL();

      // offers store is no longer valid after opting out
      this.rootStore.resetOffersStore();
    }
  };

  reset = () => {
    this.hasRewards = false;
    this.isLiteMember = false;
    this.firstName = '';
    this.lastName = '';
    this.phoneNumber = '';
    this.emailAddress = '';
    this.dateOfBirth = '';
    // DIS-1592: suburb is transmitted on both lite signup calls, so leaving it
    // set here let a second lite signup in the same app session send the
    // previous user's suburb.
    this.suburb = null;
    this.signUpCode = '';
    this.cardNumber = '';
    this.shortcode = '';
    this.shortcodeExpiresAt = '';
    this.walletUrl = '';
    this.memberSince = '';
    this.isStaffSignup = false;
    this.staffStoreId = null;
    this.staffStore = null;
    this.staffSessionToken = '';
    // DIS-1384: clear all LMG mode/badge/selection state on full reset.
    this.isLmgStaffFlow = false;
    this.isLmgStaff = false;
    this.lmgDepartment = '';
    this.lmgState = '';
    this.lmgStaffCode = '';
    this.lmgDepartments = [];
    this.lmgStates = [];
    this.lmgOptionsLoading = false;
    this.lmgOptionsError = false;
    clearInflight();
  };

  // services
  signUp = async (verification: string, flowType: AccountStatus): Promise<RewardsSignUpOutcome> => {
    const result = await apiService.rewardsSignUp({
      code: verification,
      phoneNumber: this.phoneNumber,
      firstName: this.firstName,
      lastName: this.lastName,
      emailAddress: this.emailAddress,
      dateOfBirth: this.dateOfBirth,
      suburb: this.suburb,
      ...(this.signUpCode ? {signUpCode: this.signUpCode} : {}),
      ...(this.isStaffSignup && this.staffStoreId ? {staffStoreId: this.staffStoreId} : {}),
      flowType,
    });

    if (!result.ok) {
      // DIS-1328: 422 account_not_found diverts to the terminal support screen;
      // every other failure (wrong code, network, 5xx) renders the inline message.
      return result.reason === 'account_not_found' ? 'account_not_found' : 'failed';
    }

    const {
      shortcodeExpiresAt,
      shortcode,
      cardNumber,
      firstName,
      lastName,
      status,
      walletUrl,
      memberSince,
      isStaff,
    } = result.data;

    this.setCardNumber(cardNumber);
    this.setShortcode(shortcode);
    this.setShortcodeExpiresAt(shortcodeExpiresAt);
    // DIS-1321: WIRE_OR_LOCAL keeps DIS-1187 form-preservation semantics for
    // signup endpoints (verification-lite does not yet echo Zen names for
    // existing-user staff paths — MW PR #565 fixes that, until then form
    // value wins). OR_RATCHET keeps DIS-1239 staff-pill behavior.
    this.setFirstName(mergeStringField(this.firstName, firstName, 'WIRE_OR_LOCAL'));
    this.setLastName(mergeStringField(this.lastName, lastName, 'WIRE_OR_LOCAL'));
    this.setWalletUrl(walletUrl);
    this.memberSince = mergeStringField(this.memberSince, memberSince, 'WIRE_OR_LOCAL');
    this.isStaffSignup = mergeStaffFlag(this.isStaffSignup, isStaff, 'OR_RATCHET');
    // DIS-1646: no LMG signup completes through this endpoint (LMG goes through
    // signUpStaff). Derive the badge from the running flow so a stale local true
    // is not re-persisted by the dump() below.
    if (!this.isLmgStaffFlow) this.isLmgStaff = false;

    this.dump();
    // DIS-1228: signup complete — in-flight slice no longer needed.
    clearInflight();

    // DIS-1187: name backfill via /rewards/account-details was attempted
    // here but that endpoint requires MFL session auth (401 for lite users
    // — same root cause as DIS-1176 lite-user session gap). For staff
    // existing-user path verification-lite must surface firstName/lastName
    // server-side; no client-only path exists. Success screen falls back
    // to "Welcome!" + empty MemberCard name line until middleware fix
    // lands. See linked MW-* ticket.

    return status === 'approved' ? 'success' : 'failed';
  };

  // DIS-1190 phase 1: staff verify phone. Backend issues a sessionToken
  // tied to the parked workflow (5min TTL). Phase 3 must echo it.
  verifyStaffPhone = async (code: string): Promise<VerifyPhoneStaffResult> => {
    const result = await apiService.verifyPhoneStaff({
      phoneNumber: this.phoneNumber,
      code,
    });
    if (result.ok) {
      runInAction(() => {
        this.staffSessionToken = result.sessionToken;
      });
      // DIS-1228: persist staffSessionToken so phase 3 survives a reload
      // within the 5min backend workflow TTL.
      this.dumpInflightSnapshot();
    }
    return result;
  };

  // DIS-1190 phase 3: staff complete signup. Sends collected user details to
  // /rewards/verification-lite/complete. Phone-keyed lookup of parked
  // workflow on backend (no ticketId). On success, mirrors signUp() side
  // effects so MemberCard and Success screen render correctly.
  signUpStaff = async (flowType: AccountStatus): Promise<RewardsSignUpCompleteResult> => {
    const result = await apiService.rewardsSignUpComplete({
      sessionToken: this.staffSessionToken,
      phoneNumber: this.phoneNumber,
      firstName: this.firstName,
      lastName: this.lastName,
      emailAddress: this.emailAddress,
      dateOfBirth: this.dateOfBirth,
      suburb: this.suburb,
      ...(this.signUpCode ? {signUpCode: this.signUpCode} : {}),
      // DIS-1384: LMG staff send dept/state/code and NEVER staffStoreId — the
      // two are mutually exclusive (contract §5.4/C6); store-staff path unchanged.
      ...(this.isLmgStaffFlow
        ? {
            lmgDepartment: this.lmgDepartment,
            lmgState: this.lmgState,
            lmgStaffCode: this.lmgStaffCode,
          }
        : this.staffStoreId
          ? {staffStoreId: this.staffStoreId}
          : {}),
      flowType,
    });

    if (!result.ok) {
      return result;
    }

    const {
      shortcodeExpiresAt,
      shortcode,
      cardNumber,
      firstName,
      lastName,
      walletUrl,
      memberSince,
      isStaff,
    } = result.data;

    runInAction(() => {
      // DIS-1135 bug_014: mirror signUp() — set hasRewards so
      // AccountRewardsScreen renders MemberCard on subsequent visits.
      this.hasRewards = true;
      // DIS-1633: lite sign-up creates no MFL session, so mark this device as a
      // member explicitly — isLoggedIn will never become true for this cohort.
      this.isLiteMember = true;
      this.setCardNumber(cardNumber);
      this.setShortcode(shortcode);
      this.setShortcodeExpiresAt(shortcodeExpiresAt);
      // DIS-1321: WIRE_OR_LOCAL preserves DIS-1187 form-collected name over
      // empty wire response; OR_RATCHET preserves DIS-1239 staff-pill behavior.
      this.setFirstName(mergeStringField(this.firstName, firstName, 'WIRE_OR_LOCAL'));
      this.setLastName(mergeStringField(this.lastName, lastName, 'WIRE_OR_LOCAL'));
      this.setWalletUrl(walletUrl);
      this.memberSince = mergeStringField(this.memberSince, memberSince, 'WIRE_OR_LOCAL');
      this.isStaffSignup = mergeStaffFlag(this.isStaffSignup, isStaff, 'OR_RATCHET');
      // DIS-1384: LMG badge derived DIRECTLY from the response (NOT a ratchet) —
      // 'skipped' sets false while isLmgStaffFlow stays true (§2.2, R14/R15).
      // DIS-1646: the else leg matters — without it a stale local true rides
      // into the dump() below on a non-LMG completion.
      this.isLmgStaff = this.isLmgStaffFlow
        ? result.data.lmgStaffActivation?.status === 'applied'
        : false;
    });

    this.dump();
    // DIS-1228: staff signup complete — in-flight slice no longer needed.
    clearInflight();
    return result;
  };

  logout = async () => {
    runInAction(() => {
      this.reset();
    });
    removeValueFromStorage(REWARDS_STORE_KEY);
  };
}
